import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Trash2, Plus, Minus, ShoppingBag, ShieldCheck, Truck, FileText } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import usePageTitle from '../hooks/usePageTitle';
import { BRAND_INFO } from '../data/mock';
import { useCart } from '../context/CartContext';

const PROCESSING_FEE = 5;

const CartPage = () => {
  usePageTitle(`Your cart | ${BRAND_INFO.name}`);
  const { items, subtotal, updateQty, removeItem, totalQty } = useCart();

  const fee = items.length > 0 ? PROCESSING_FEE : 0;
  const total = subtotal + fee;

  return (
    <>
      <Header />

      <section className="pt-28 lg:pt-36 pb-10 bg-cream-100">
        <div className="container-wide">
          <p className="eyebrow">Cart</p>
          <h1 className="mt-5 font-display text-display-lg text-ink-900">
            Your <em className="italic text-brick-500">order</em>.
          </h1>
          <p className="mt-4 text-ink-600">
            {totalQty === 0
              ? 'Your cart is empty.'
              : `${totalQty} item${totalQty > 1 ? 's' : ''} in your cart.`}
          </p>
        </div>
      </section>

      {items.length === 0 ? (
        <EmptyCart />
      ) : (
        <section className="pb-24 bg-cream-100">
          <div className="container-wide grid lg:grid-cols-12 gap-10">
            {/* Items list */}
            <div className="lg:col-span-8 space-y-4">
              {items.map((it) => (
                <article
                  key={it.id}
                  className="bg-cream-50 border border-cream-200 rounded-3xl p-6 lg:p-7 flex flex-col sm:flex-row gap-5"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[10px] tracking-[0.18em] uppercase text-ink-400">
                          {it.kind === 'panel' ? `Panel · ${it.sku || ''}` : 'Custom panel'}
                          {it.badge ? <span className="ml-2">· {it.badge}</span> : null}
                        </div>
                        <h3 className="mt-1 font-display text-2xl text-ink-900 leading-tight">{it.name}</h3>
                      </div>
                      <div className="text-right">
                        <div className="font-display text-2xl text-ink-900 tabular-nums">
                          ${(it.price * (it.qty || 1)).toFixed(0)}
                        </div>
                        <div className="text-[12px] text-ink-500 tabular-nums">
                          ${it.price} each
                        </div>
                      </div>
                    </div>

                    {/* Custom panel breakdown */}
                    {it.kind === 'custom' && it.markers && (
                      <details className="mt-4 group">
                        <summary className="cursor-pointer list-none text-[13px] text-brick-500 hover:text-brick-600 inline-flex items-center gap-1">
                          <FileText className="w-3.5 h-3.5" />
                          {it.markers.length} marker{it.markers.length > 1 ? 's' : ''} included
                          <span className="ml-1 text-ink-400 group-open:rotate-180 transition-transform">▾</span>
                        </summary>
                        <ul className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-[13px] text-ink-700">
                          {it.markers.map((m) => (
                            <li
                              key={m.id}
                              className="flex items-center justify-between border-b border-cream-200 py-1"
                            >
                              <span className="truncate pr-2">{m.name}</span>
                              <span className="tabular-nums text-ink-500">${m.price}</span>
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}

                    {/* Qty + remove */}
                    <div className="mt-5 flex items-center justify-between">
                      <div className="inline-flex items-center bg-cream-100 border border-cream-200 rounded-full overflow-hidden">
                        <button
                          onClick={() => updateQty(it.id, (it.qty || 1) - 1)}
                          disabled={(it.qty || 1) <= 1}
                          className="w-9 h-9 inline-flex items-center justify-center text-ink-700 hover:bg-cream-200 disabled:opacity-30"
                          aria-label="Decrease quantity"
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span className="min-w-[2.5rem] text-center text-[14px] text-ink-900 tabular-nums">
                          {it.qty || 1}
                        </span>
                        <button
                          onClick={() => updateQty(it.id, (it.qty || 1) + 1)}
                          className="w-9 h-9 inline-flex items-center justify-center text-ink-700 hover:bg-cream-200"
                          aria-label="Increase quantity"
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <button
                        onClick={() => removeItem(it.id)}
                        className="inline-flex items-center gap-1.5 text-[13px] text-ink-500 hover:text-brick-500 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Remove
                      </button>
                    </div>
                  </div>
                </article>
              ))}

              <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                <Link to="/panels" className="btn-ghost text-[14px]">
                  ← Continue shopping
                </Link>
                <Link to="/biomarkers" className="btn-ghost text-[14px]">
                  + Add more markers à la carte
                </Link>
              </div>
            </div>

            {/* Order summary */}
            <aside className="lg:col-span-4">
              <div className="sticky top-28 bg-cream-50 border border-cream-200 rounded-3xl p-7">
                <h3 className="font-display text-2xl text-ink-900">Order summary</h3>

                <dl className="mt-5 space-y-3 text-[14px]">
                  <div className="flex items-center justify-between text-ink-700">
                    <dt>Subtotal</dt>
                    <dd className="tabular-nums text-ink-900">${subtotal.toFixed(2)}</dd>
                  </div>
                  <div className="flex items-center justify-between text-ink-700">
                    <dt>Lab processing</dt>
                    <dd className="tabular-nums text-ink-900">${fee.toFixed(2)}</dd>
                  </div>
                  <div className="flex items-center justify-between text-ink-700">
                    <dt>Estimated tax</dt>
                    <dd className="tabular-nums text-ink-500">Calculated next</dd>
                  </div>
                </dl>

                <div className="mt-5 pt-5 border-t border-cream-200 flex items-end justify-between">
                  <div>
                    <div className="text-[10px] tracking-[0.18em] uppercase text-ink-400">Total</div>
                    <div className="text-[12px] text-ink-500">USD · due today</div>
                  </div>
                  <div className="font-display text-3xl text-ink-900">
                    <span className="text-lg align-top">$</span>
                    {total.toFixed(2)}
                  </div>
                </div>

                <Link
                  to="/checkout"
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-brick-500 hover:bg-brick-600 text-white font-medium px-5 py-3.5 rounded-full transition-colors"
                >
                  Checkout <ArrowRight className="w-4 h-4" />
                </Link>

                <ul className="mt-6 space-y-3 text-[12px] text-ink-600">
                  <li className="flex items-start gap-2">
                    <ShieldCheck className="w-4 h-4 text-brick-500 mt-0.5" />
                    Physician-signed requisition included with every panel.
                  </li>
                  <li className="flex items-start gap-2">
                    <Truck className="w-4 h-4 text-brick-500 mt-0.5" />
                    Get drawn at any partner Quest, LabCorp, or BioReference location.
                  </li>
                  <li className="flex items-start gap-2">
                    <FileText className="w-4 h-4 text-brick-500 mt-0.5" />
                    Results stream to your dashboard — typically within 48 hours.
                  </li>
                </ul>
              </div>
            </aside>
          </div>
        </section>
      )}

      <Footer />
    </>
  );
};

const EmptyCart = () => (
  <section className="pb-24 bg-cream-100">
    <div className="container-tight">
      <div className="bg-cream-50 border border-cream-200 rounded-3xl p-10 lg:p-14 text-center">
        <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-cream-100 border border-cream-200 text-ink-700">
          <ShoppingBag className="w-7 h-7" />
        </span>
        <h2 className="mt-6 font-display text-display-md text-ink-900">
          Your cart is empty.
        </h2>
        <p className="mt-3 text-ink-600 max-w-md mx-auto">
          Browse curated lab panels or build your own from 140+ individual
          biomarkers — it takes less than two minutes.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link to="/panels" className="btn-brick">
            Browse panels <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/biomarkers" className="btn-light">
            Build à la carte
          </Link>
        </div>
      </div>
    </div>
  </section>
);

export default CartPage;
